<template>
  <!-- 移动端遮罩层 -->
  <div
    v-if="sidebarStore.isMobileDrawerOpen"
    class="mobile-overlay"
    @click="sidebarStore.closeMobileDrawer"
  ></div>

  <aside
    class="sidebar"
    :class="{
      collapsed: sidebarStore.isCollapsed,
      'mobile-drawer-open': sidebarStore.isMobileDrawerOpen
    }"
  >
    <div class="sidebar-header">
      <div class="sidebar-logo" @click="goToHome">
        <img
          src="@/assets/images/logo.png"
          alt="安天模具"
          class="logo-icon"
          :class="{ 'logo-small': sidebarStore.isCollapsed }"
        />
        <span v-show="!sidebarStore.isCollapsed" class="logo-text">安天模具管理系统</span>
      </div>
      <!-- 移动端关闭按钮 -->
      <div class="mobile-close-btn" @click="sidebarStore.closeMobileDrawer">
        <el-icon :size="20"><Close /></el-icon>
      </div>
    </div>

    <div class="sidebar-menu">
      <template v-for="menu in filteredMenus" :key="menu.key">
        <div 
          v-if="!menu.children || menu.children.length === 0"
          class="menu-item-level-1" 
          :class="{ active: activeMenu === menu.path }"
        >
          <el-tooltip
            :content="menu.label"
            placement="right"
            :show-after="300"
          >
            <div class="menu-item-level-1-header" @click="handleMenuClick(menu.path!)">
              <div class="menu-item-icon">
                <component :is="getIconComponent(menu.icon!)" :size="20" />
              </div>
              <span v-show="!sidebarStore.isCollapsed" class="menu-item-title">{{ menu.label }}</span>
            </div>
          </el-tooltip>
        </div>

        <div
          v-else
          class="menu-item-level-1"
          :class="{ active: activeMenu.startsWith(`/${menu.key}`), expanded: expandedMenus[menu.key] }"
          @mouseenter="handleMenuHoverEnter(menu.key)"
          @mouseleave="handleMenuHoverLeave"
        >
          <el-tooltip
            :content="menu.label"
            placement="right"
            :show-after="300"
            :disabled="sidebarStore.isCollapsed"
          >
            <div class="menu-item-level-1-header" @click="toggleMenu(menu.key)">
              <div class="menu-item-icon">
                <component :is="getIconComponent(menu.icon!)" :size="20" />
              </div>
              <span v-show="!sidebarStore.isCollapsed" class="menu-item-title">{{ menu.label }}</span>
              <div class="menu-item-arrow" v-if="hasVisibleChildren(menu)">
                <el-icon :size="16">
                  <ArrowRight />
                </el-icon>
              </div>
            </div>
          </el-tooltip>

          <div
            v-show="(expandedMenus[menu.key] && !sidebarStore.isCollapsed) || hoveredMenuKey === menu.key"
            class="submenu"
            :class="{ expanded: expandedMenus[menu.key] }"
            @mouseenter="handleSubmenuEnter(menu.key)"
            @mouseleave="handleSubmenuLeave"
          >
            <template
              v-for="child in getFilteredChildren(menu)"
              :key="child.key"
            >
              <!-- 分组：组标题改为纯视觉小标签（不可点击、默认全展开），叶子全露出 -->
              <template
                v-if="child.children && child.children.length && getFilteredChildren(child).length"
              >
                <div class="menu-group-title">
                  <span class="menu-group-label">{{ child.label }}</span>
                </div>
                <div
                  class="menu-group-items"
                >
                  <div
                    v-for="leaf in getFilteredChildren(child)"
                    :key="leaf.key"
                    class="menu-item-level-2"
                    :class="{ active: activeMenu === leaf.path }"
                  >
                    <div class="menu-item-level-2-header" @click="handleMenuClick(leaf.path!)">
                      <div class="menu-item-level-2-icon">
                        <component :is="getIconComponent(leaf.icon!)" :size="16" />
                      </div>
                      <span class="menu-item-level-2-title">{{ leaf.label }}</span>
                    </div>
                  </div>
                </div>
              </template>
              <!-- 平铺叶子（无分组的菜单项，如模库/调模等） -->
              <div
                v-else
                class="menu-item-level-2"
                :class="{ active: activeMenu === child.path }"
              >
                <div class="menu-item-level-2-header" @click="handleMenuClick(child.path!)">
                  <div class="menu-item-level-2-icon">
                    <component :is="getIconComponent(child.icon!)" :size="16" />
                  </div>
                  <span class="menu-item-level-2-title">{{ child.label }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>

    <!-- 折叠/展开按钮 -->
    <div class="sidebar-toggle" @click="sidebarStore.toggleCollapse">
      <el-tooltip
        :content="sidebarStore.isCollapsed ? '展开菜单' : '折叠菜单'"
        placement="right"
        :show-after="300"
      >
        <div class="toggle-btn" :class="{ collapsed: sidebarStore.isCollapsed }">
          <el-icon :size="18">
            <Fold v-if="!sidebarStore.isCollapsed" />
            <Expand v-else />
          </el-icon>
        </div>
      </el-tooltip>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  HomeFilled,
  Operation,
  ArrowRight,
  DataBoard,
  List,
  Folder,
  DocumentAdd,
  Document,
  Setting,
  FolderOpened,
  Grid,
  Crop,
  Tools,
  Clock,
  Notebook,
  Promotion,
  Monitor,
  Odometer,
  Box,
  Download,
  Upload,
  TrendCharts,
  DataAnalysis,
  DataLine,
  User,
  UserFilled,
  Avatar,
  Lock,
  OfficeBuilding,
  DocumentCopy,
  Switch,
  Fold,
  Expand,
  Close
} from '@element-plus/icons-vue'
import { useSidebarResponsive } from '@/stores/sidebar'
import { menuConfig, type MenuItem } from '@/constants/menuConfig'
import { usePermission } from '@/composables'

const router = useRouter()
const route = useRoute()
const { hasPermission } = usePermission()

// 使用侧边栏响应式状态
const sidebarStore = useSidebarResponsive()

// 跳转到首页
const goToHome = () => {
  router.push('/home')
}

const activeMenu = computed(() => route.path)

const expandedMenus = ref<Record<string, boolean>>({
  processing: false,
  mold: false,
  tuning: false,
  forming: false,
  equipment: false,
  consumable: false,
  performance: false,
  system: false,
  report: false
})

const filteredMenus = computed(() => {
  return menuConfig.filter(menu => {
    if (!menu.children) {
      return !menu.permission || hasPermission(menu.permission)
    }
    
    const filteredChildren = menu.children.filter(child => {
      return !child.permission || hasPermission(child.permission)
    })
    
    return filteredChildren.length > 0
  })
})

const getFilteredChildren = (menu: MenuItem) => {
  if (!menu.children) return []
  return menu.children.filter(child => {
    return !child.permission || hasPermission(child.permission)
  })
}

// 分组标题已改为纯视觉小标签（不可折叠、默认全展开），分组折叠状态逻辑移除

const hasVisibleChildren = (menu: MenuItem) => {
  return menu.children && getFilteredChildren(menu).length > 0
}

const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, any> = {
    HomeFilled,
    Operation,
    ArrowRight,
    DataBoard,
    List,
    Folder,
    DocumentAdd,
    Document,
    Setting,
    FolderOpened,
    Grid,
    Crop,
    Tools,
    Clock,
    Notebook,
    Promotion,
    Monitor,
    Odometer,
    Box,
    Download,
    Upload,
    TrendCharts,
    DataAnalysis,
    DataLine,
    User,
    UserFilled,
    Avatar,
    Lock,
    OfficeBuilding,
    DocumentCopy,
    Switch
  }
  return iconMap[iconName] || List
}

// 折叠模式下的 hover 子菜单管理
const hoveredMenuKey = ref<string | null>(null)
let hoverTimeout: ReturnType<typeof setTimeout> | null = null

const clearHoverTimeout = () => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }
}

const handleMenuHoverEnter = (menuKey: string) => {
  if (!sidebarStore.isCollapsed) return
  clearHoverTimeout()
  hoveredMenuKey.value = menuKey
}

const handleMenuHoverLeave = () => {
  if (!sidebarStore.isCollapsed) return
  clearHoverTimeout()
  hoverTimeout = setTimeout(() => {
    hoveredMenuKey.value = null
  }, 250)
}

const handleSubmenuEnter = (menuKey: string) => {
  clearHoverTimeout()
  hoveredMenuKey.value = menuKey
}

const handleSubmenuLeave = () => {
  clearHoverTimeout()
  hoverTimeout = setTimeout(() => {
    hoveredMenuKey.value = null
  }, 200)
}

const toggleMenu = (menuKey: string) => {
  const isCurrentlyExpanded = expandedMenus.value[menuKey]

  // 先关闭所有菜单（手风琴效果：点击一个菜单时关闭其他所有菜单）
  for (const key in expandedMenus.value) {
    expandedMenus.value[key] = false
  }

  // 如果当前菜单未展开，则展开它；如果已展开，则保持关闭（实现切换）
  if (!isCurrentlyExpanded) {
    expandedMenus.value[menuKey] = true
  }
}

const handleMenuClick = (path: string) => {
  router.push(path)

  // 移动端点击菜单后关闭抽屉
  if (sidebarStore.isMobile) {
    sidebarStore.closeMobileDrawer()
  }
}

watch(() => route.path, (newPath) => {
  // 当前路由所在的一级模块保持展开，其余收起
  for (const menuKey in expandedMenus.value) {
    expandedMenus.value[menuKey] = newPath.startsWith(`/${menuKey}`)
  }
}, { immediate: true })
</script>

<style scoped>
/*
 * 侧边栏样式 - 基于2026-04工序预设置原型设计
 * Sidebar Styles - Based on Process Preset Prototype Design
 */

.sidebar {
  width: var(--layout-sidebar-width, 240px);
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  /* 浅色白底 - 对齐 UI/UX 原型（2026-10-18 用户拍板） */
  background: #ffffff;
  border-right: 1px solid var(--color-border-lighter, var(--color-border-lighter));
  display: flex;
  flex-direction: column;
  z-index: 2000 !important; /* 强制最高层级，确保在header之上 */
  overflow: visible; /* 允许子菜单溢出显示 */
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
}

/* 侧边栏头部 */
.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid var(--color-border-lighter, var(--color-border-lighter));
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* Logo样式 - 匹配原型 */
.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text-primary, var(--color-text-primary));
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  border-radius: 0;
  transition: all 0.3s ease;
}

.sidebar-logo .logo-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
  transition: filter 0.3s ease;
}

.sidebar-logo:hover .logo-icon {
  filter: drop-shadow(0 0 8px rgba(35, 131, 226, 0.6));
}

.sidebar-logo span {
  color: var(--color-text-primary, var(--color-text-primary));
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.sidebar-logo:hover {
  background: rgba(35, 131, 226, 0.1);
  border-radius: 8px;
}

/* 移动端关闭按钮 - 默认隐藏 */
.mobile-close-btn {
  display: none;
}

/* 导航菜单区 */
.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 0;
}

.sidebar-menu::-webkit-scrollbar {
  width: 6px;
}

.sidebar-menu::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-menu::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.12);
  border-radius: 2px;
}

.sidebar-menu::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.22);
}

/* 一级菜单项 - 匹配原型设计 */
.menu-item-level-1 {
  margin-bottom: var(--spacing-xs);
  padding: 0;
}

.menu-item-level-1-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: var(--color-text-regular, var(--color-text-regular));
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  border-radius: 0;
  position: relative;
}

/* Hover状态 - 匹配原型 */
.menu-item-level-1-header:hover {
  background: rgba(35, 131, 226, 0.08);
  color: var(--color-primary);
  transform: none;
}

/* Active状态 - 匹配原型 */
.menu-item-level-1-header.active {
  background: rgba(35, 131, 226, 0.12);
  color: var(--color-primary);
  border-left-color: var(--color-primary);
}

.menu-item-level-1-header.active::before {
  content: none;
}

/* 图标样式 */
.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--color-text-secondary, var(--color-text-secondary));
}

.menu-item-level-1-header:hover .menu-item-icon {
  color: var(--color-primary);
}

.menu-item-level-1-header.active .menu-item-icon {
  color: var(--color-primary);
}

/* 菜单标题 */
.menu-item-title {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: inherit;
  white-space: nowrap;
}

/* 箭头 */
.menu-item-arrow {
  margin-left: auto;
  transition: transform 0.3s;
  color: var(--color-text-secondary, var(--color-text-secondary));
}

.menu-item-level-1.expanded .menu-item-arrow {
  transform: rotate(90deg);
}

/* 二级菜单 */
.submenu {
  margin-left: 0;
  padding-left: 32px;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.submenu.expanded {
  max-height: 1200px;
}

/* 分组标题（纯视觉小标签，不可点击） */
.menu-group-title {
  display: flex;
  align-items: center;
  padding: 8px 20px 4px 24px;
  font-size: 12px;
  letter-spacing: 1px;
  color: var(--color-text-placeholder, #a8abb2);
  white-space: nowrap;
  user-select: none;
}

.menu-group-title::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 1px;
  background: var(--color-border, var(--color-border-base));
  margin-right: 8px;
}

.menu-group-title .menu-group-label {
  pointer-events: none;
}

.menu-group-items {
  /* 分组子项容器，仅作语义包裹 */
}

.menu-item-level-2 {
  margin-bottom: var(--spacing-xs);
  padding: 0;
}

.menu-item-level-2-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 20px;
  color: var(--color-text-regular, var(--color-text-regular));
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
  border-radius: 0;
  position: relative;
}

.menu-item-level-2-header:hover {
  background: rgba(35, 131, 226, 0.08);
  color: var(--color-primary);
  transform: none;
}

.menu-item-level-2-header.active {
  background: rgba(35, 131, 226, 0.12);
  color: var(--color-primary);
  border-left-color: var(--color-primary);
}

.menu-item-level-2-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--color-text-secondary, var(--color-text-secondary));
}

.menu-item-level-2-header:hover .menu-item-level-2-icon {
  color: var(--color-primary);
}

.menu-item-level-2-header.active .menu-item-level-2-icon {
  color: var(--color-primary);
}

.menu-item-level-2-title {
  flex: 1;
  font-size: 13px;
  font-weight: 400;
  color: inherit;
  white-space: nowrap;
}

.menu-item-level-2-header.active .menu-item-level-2-title {
  font-weight: 500;
}


/* ==================== 侧边栏折叠功能样式 ==================== */

/* 折叠状态下的侧边栏 */
.sidebar.collapsed {
  width: 64px;
}

.sidebar.collapsed .sidebar-header {
  padding: 20px 16px;
}

.sidebar.collapsed .sidebar-logo {
  justify-content: center;
}

.sidebar.collapsed .logo-icon.logo-small {
  width: 28px;
  height: 28px;
}

.sidebar.collapsed .sidebar-menu {
  padding: 16px 8px;
}

.sidebar.collapsed .menu-item-level-1-header {
  padding: 12px 16px;
  justify-content: center;
}

.sidebar.collapsed .menu-item-icon {
  width: 24px;
  height: 24px;
}

.sidebar.collapsed .menu-item-icon .el-icon {
  font-size: 20px;
}

.sidebar.collapsed .menu-item-arrow {
  display: none;
}

/* 折叠按钮区域 */
.sidebar-toggle {
  margin-top: auto;
  padding: 12px;
  border-top: 1px solid var(--color-border-lighter, var(--color-border-lighter));
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--color-text-secondary, var(--color-text-secondary));
  background: rgba(0, 0, 0, 0.04);
}

.toggle-btn:hover {
  background: rgba(35, 131, 226, 0.12);
  color: var(--color-primary);
}

.toggle-btn.collapsed {
  transform: rotateY(180deg);
}

/* 折叠状态下的子菜单 - 弹出式 */
.sidebar.collapsed .menu-item-level-1 {
  position: relative;
}

.sidebar.collapsed .submenu {
  position: fixed;
  left: 56px;
  top: auto;
  width: 180px;
  background: #ffffff;
  padding: 8px 8px 8px 16px;
  border-radius: 8px;
  border: 1px solid var(--color-border-lighter, var(--color-border-lighter));
  box-shadow: 4px 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 2001;
  transition: none;
}

.sidebar.collapsed .menu-group-title {
  padding: 8px 12px 4px 12px;
  font-size: 11px;
  color: var(--color-text-placeholder, #a8abb2);
}

.sidebar.collapsed .menu-item-level-2 {
  margin-bottom: 4px;
}

.sidebar.collapsed .menu-item-level-2-header {
  padding: 8px 12px;
  border-radius: 6px;
}

.sidebar.collapsed .menu-item-level-2-title {
  font-size: 12px;
}

/* 确保hover时显示子菜单 */
.sidebar.collapsed .menu-item-level-1:hover .submenu {
  display: block !important;
}

/* 折叠动画过渡 */
.sidebar,
.sidebar * {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 移动端适配 - 抽屉模式 */
@media (max-width: 767px) {
  .sidebar,
  .sidebar.collapsed {
    width: 280px;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 2100 !important;
    overflow: hidden;
  }

  .sidebar.mobile-drawer-open {
    transform: translateX(0);
  }

  /* 移动端显示完整菜单文字和箭头 */
  .sidebar .logo-text {
    display: inline !important;
  }

  .sidebar .menu-item-title {
    display: inline !important;
  }

  .sidebar .menu-item-arrow {
    display: flex !important;
  }

  /* 菜单区域可滚动 */
  .sidebar .sidebar-menu {
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  /* 一级菜单分隔线 */
  .sidebar .menu-item-level-1 {
    border-bottom: 1px solid var(--color-border-extra-light, #f2f6fc);
    margin-bottom: 0 !important;
  }

  .sidebar .menu-item-level-1-header {
    min-height: 48px;
    padding: 12px 16px;
  }

  /* 二级菜单背景区分 */
  .sidebar .submenu {
    padding-left: 24px !important;
    background: rgba(0, 0, 0, 0.02);
  }

  .sidebar .menu-item-level-2-header {
    min-height: 44px;
    padding: 8px 16px;
  }

  /* 隐藏折叠按钮 */
  .sidebar-toggle {
    display: none;
  }

  /* 移动端关闭按钮 */
  .mobile-close-btn {
    display: flex;
    position: absolute;
    top: 20px;
    right: 12px;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--color-text-secondary, var(--color-text-secondary));
    cursor: pointer;
  }

  .mobile-close-btn:active {
    background: rgba(0, 0, 0, 0.06);
    color: var(--color-text-primary, var(--color-text-primary));
  }
}

/* 移动端遮罩层 */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2050;
  transition: opacity 0.3s ease;
}

/* 桌面端展开状态 */
@media (min-width: 768px) {
  .sidebar:not(.collapsed) {
    width: 240px;
  }
}
</style>
/* 弹出子菜单样式 */
.menu-item-hover-trigger {
  cursor: pointer;
}

.popover-menu-container {
  padding: 4px 0;
}

.popover-menu-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-border-lighter);
  margin-bottom: 4px;
}

.popover-submenu-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.popover-submenu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: var(--color-text-secondary);
  transition: all 0.2s ease;
}

.popover-submenu-item:hover {
  background-color: var(--color-primary-lighter);
  color: var(--color-primary);
}

.popover-submenu-item.active {
  background-color: var(--color-primary-lightest);
  color: var(--color-primary);
  font-weight: 500;
}
